import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSend, FiCheck } = FiIcons;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    subject: 'Contact Form Submission',
    contact_homepage_url: '' // honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot
    if (formData.contact_homepage_url) {
      console.log('Spam submission detected');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const ZAPIER_URL = 'https://hooks.zapier.com/hooks/catch/24147191/u6t6dh2/';
      const THANK_YOU_URL = 'https://hvacbizbroker.com/thank-you';
      
      const response = await fetch(ZAPIER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        mode: 'cors',
        keepalive: true
      });
      
      setSubmitSuccess(true);
      
      // Redirect to thank you page after a brief delay
      setTimeout(() => {
        window.location.href = THANK_YOU_URL;
      }, 1000);
    } catch (error) {
      console.error('Form submission error:', error);
      // Even on error, redirect to thank you page as fallback
      window.location.href = 'https://hvacbizbroker.com/thank-you';
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-lg">
      {submitSuccess ? (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 text-green-600 rounded-full mb-6">
            <SafeIcon icon={FiCheck} className="w-8 h-8" />
          </div>
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">
            Message Sent!
          </h3>
          <p className="text-gray-600 mb-4">
            Thank you for contacting us. We will get back to you shortly.
          </p>
          <p className="text-gray-500 text-sm">
            Redirecting you to our thank you page...
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="4"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="How can we help you?"
            ></textarea>
          </div>
          
          {/* Honeypot field */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, overflow: "hidden" }}>
            <input
              type="text"
              name="contact_homepage_url"
              value={formData.contact_homepage_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>
          
          {submitError && (
            <div className="text-red-600 text-sm py-2">{submitError}</div>
          )}
          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={isSubmitting}
            className={`w-full ${
              isSubmitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
            } text-white px-6 py-4 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-colors duration-300`}
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiSend} className="w-5 h-5" />
                <span>Send Message</span>
              </>
            )}
          </motion.button>
        </form>
      )}
    </div>
  );
};

export default ContactForm;